// moms-harness:anchor v2
// Spec: docs/spec/settlement/SPEC-settlement-mwp-mlt-sku-attribute-registration-A.yaml
// Spec: docs/spec/settlement/SPEC-settlement-mwp-mlt-sku-attribute-registration-C.yaml
// JIRA: LOGISPRD-13286, LOGISPRD-13480, LOGISPRD-13852, LOGISPRD-14923, LOGISPRD-14926
// ============================================================
//  MA18 신규 상품관리 — 조회 데이터 접점
// ------------------------------------------------------------
//  BE: GET /api/v1/master/sku-attributes (LOGISPRD-13284, sku_attribute)
//  - 한 행 = sku_attribute 한 행 = SKU 하나. MA02 처럼 옵션으로 불어나지 않는다
//  - 필터: shipperCode = eq, skuIds = IN(정확일치, LOGISPRD-13851), 나머지 텍스트 축
//    (barcode/itemCode/itemName/brandName/color/styleNumber) = contains.
//    BE 는 unitCode 도 받지만 화면 필터에서는 뺐다 (LOGISPRD-13852)
//  - shipperCode 와 serviceTypes 를 함께 주면 **shipperCode 가 이긴다** (BE 규칙)
//  - 정렬은 서버 고정(shipper_code, barcode) — sort 파라미터는 무시된다
//  - 페이지 크기 상한 2,000 (Spring Data 기본 max-page-size) — 화면은 그 크기로 한 페이지씩 받는다 (LOGISPRD-14923)
// ============================================================
import { useEffect } from 'react'
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import request from '@/utils/request'
import type { SkuAttributeRow } from './types'
import { LIST_CACHE_TIME_MS, MENU_CODE, PAGE_SIZE, SERVICE_TYPE_OPTIONS } from '../components/consts'

/** BE 응답 18필드 그대로. 화면의 20열 중 나머지 2열(화주명·화주타입)은 FE 가 조인해 만든다. */
export interface SkuAttributeItem {
  skuId: string
  shipperCode: string
  barcode: string
  itemCode?: string | null
  itemOption?: string | null
  itemName?: string | null
  category?: string | null
  // LOGISPRD-13480. 이관 행은 전부 null 이다 — 이관이 아직 이 셋을 옮기지 않는다(별건 티켓)
  color?: string | null
  styleNumber?: string | null
  unitCode?: string | null
  brandId?: string | null
  brandName?: string | null
  receivablePrice?: number | null
  payablePrice?: number | null
  // BE LocalDateTime 배열 직렬화([y,M,d,H,m,s]) — formatDateTime 이 배열/문자열 겸용 처리
  createdAt?: string | number[] | null
  createdBy?: string | null
  lastModifiedAt?: string | number[] | null
  lastModifiedBy?: string | null
}

// 행 타입은 queries/types.ts 로 내렸다 — consts.ts 와의 순환 import 를 끊기 위해서다
// (PR #810 3차 봇 리뷰). 기존 import 경로를 깨지 않게 여기서 그대로 다시 내보낸다
export type { SkuAttributeRow } from './types'

export interface SkuAttributeParams {
  /** 조회 시퀀스 — 동일 조건 재조회용 queryKey 구분자 (서버 미전달) */
  _seq?: number
  /** 화주유형 (BE serviceTypes). BE 가 MWP·MLT 와 교집합을 낸다 */
  serviceTypes?: string[]
  /** 화주코드 (BE shipperCode eq) — **단건만 보낸다**. BE 가 단일 값 파라미터다 */
  shipperCode?: string
  barcode?: string
  itemCode?: string
  itemName?: string
  brandName?: string
  color?: string
  styleNumber?: string
  /**
   * SKU ID 단건·다건 (BE `skuIds`, 정확일치 IN — LOGISPRD-13851/13852). 화주 없이도 조회할 수 있다 —
   * 사용자가 그 SKU 의 화주를 모를 수 있고, 화주 범위(MWP·MLT)는 BE 가 그대로 건다.
   *
   * 상품단위(`unitCode`)는 필터에서 뺐다 (LOGISPRD-13852) — 화면이 보낼 경로가 없으므로 이 타입에서도 없앤다.
   * 남겨 두면 옛 URL 의 `unitCode` 가 **보이지 않는 조건**으로 복원된다.
   */
  skuIds?: string[]
}

/** 한 페이지 조회 결과. `contents` 는 그 페이지 행만, `totalCount` 는 조건 전체 건수다 — 페이저가 이것으로 페이지를 나눈다 */
export interface SkuAttributeListResponse {
  contents: SkuAttributeRow[]
  totalCount: number
}

/** 화주코드 → {shipperName, serviceType} 조인용 (두 표시 컬럼은 BE 응답에 없어 FE 파생) */
export interface ShipperInfoMap {
  [shipperCode: string]: { shipperName: string; serviceType: string }
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/**
 * BE `LocalDateTime` → 표시 문자열.
 *
 * Jackson 이 `LocalDateTime` 을 **배열 `[y,M,d,H,m,s]`** 로 직렬화하므로 배열과 문자열을 함께 받는다.
 *
 * `userManagement/constants` 의 `formatDateTime` 과 같은 로직이지만 **복사본이다** — 다른 화면의
 * 상수 파일에 의존하면 그 화면을 고치는 사람이 MA18 도 바뀐다는 것을 모른다. 7줄짜리 순수 함수라
 * 격리 비용이 거의 없다. (빈 값을 `'-'` 가 아니라 `''` 로 두는 것만 다르다 — 그리드 빈 셀 표기)
 */
const fmt = (v?: string | number[] | null): string => {
  if (v == null) return ''
  if (Array.isArray(v)) {
    const [y, mo = 1, d = 1, h = 0, mi = 0, sec = 0] = v
    return `${y}-${pad2(mo)}-${pad2(d)} ${pad2(h)}:${pad2(mi)}:${pad2(sec)}`
  }
  return v ? v.slice(0, 19).replace('T', ' ') : ''
}

interface PageInfo {
  totalCount: number
}

/**
 * 조회 조건 → 서버 쿼리 파라미터.
 *
 * **목록과 엑셀이 같은 함수를 쓴다** — 필터 조합이 두 곳에 있으면 파라미터가 늘 때 한쪽만
 * 갱신되고, 화면에서 본 것과 받은 엑셀이 달라진다.
 *
 * 빈 문자열·undefined 는 보내지 않는다. BE 가 정규화하지만 쿼리스트링을 지저분하게 만들 이유가 없다.
 * `shipperCode` 가 있으면 `serviceTypes` 를 **보내지 않는다** — BE 규칙상 `shipperCode` 가 이기므로
 * 둘 다 보내면 무시되는 파라미터가 요청에 남아 의도를 읽기 어렵다.
 */
export const toQuery = (params: SkuAttributeParams): Record<string, unknown> => {
  const q: Record<string, unknown> = {}
  if (params.shipperCode) q.shipperCode = params.shipperCode
  else if (params.serviceTypes?.length) q.serviceTypes = params.serviceTypes
  if (params.barcode) q.barcode = params.barcode
  if (params.itemCode) q.itemCode = params.itemCode
  if (params.itemName) q.itemName = params.itemName
  if (params.brandName) q.brandName = params.brandName
  if (params.color) q.color = params.color
  if (params.styleNumber) q.styleNumber = params.styleNumber
  if (params.skuIds?.length) q.skuIds = params.skuIds
  return q
}

export const toRow = (item: SkuAttributeItem, shipperInfo: ShipperInfoMap): SkuAttributeRow => ({
  _skuId: item.skuId,
  service_type: shipperInfo[item.shipperCode]?.serviceType ?? '',
  shipper_name: shipperInfo[item.shipperCode]?.shipperName ?? '',
  shipper_code: item.shipperCode,
  sku_id: item.skuId,
  barcode: item.barcode,
  item_code: item.itemCode ?? '',
  item_option: item.itemOption ?? '',
  item_name: item.itemName ?? '',
  category: item.category ?? '',
  color: item.color ?? '',
  style_number: item.styleNumber ?? '',
  unit_code: item.unitCode ?? '',
  brand_id: item.brandId ?? '',
  brand_name: item.brandName ?? '',
  receivable_price: item.receivablePrice ?? null,
  payable_price: item.payablePrice ?? null,
  created_at: fmt(item.createdAt),
  created_by: item.createdBy ?? '',
  last_modified_at: fmt(item.lastModifiedAt),
  last_modified_by: item.lastModifiedBy ?? '',
})

/**
 * 한 페이지(`page` 는 1부터)만 받는다 — **전체를 순회하지 않는다** (LOGISPRD-14923).
 *
 * 초판은 전 페이지를 순회해 한 표에 실었고, 순회 종료를 「받은 건수 < 요청 크기」로 판단했다. 서버가 요청 크기를
 * 2,000 으로 줄여 주자 첫 페이지가 곧 마지막으로 읽혀 나머지가 경고 없이 빠졌다. 페이지마다 받으면 순회도, 그
 * 순회의 안전 상한(과 상한 초과 경고)도 필요 없다 — 건수가 얼마든 페이지를 넘겨 끝까지 볼 수 있다.
 */
const fn = async (
  params: SkuAttributeParams,
  page: number,
  shipperInfo: ShipperInfoMap,
  signal?: AbortSignal,
): Promise<SkuAttributeListResponse> => {
  const res = await request.get<ApiResponseV2<{ items: SkuAttributeItem[]; pageInfo: PageInfo }>, Record<string, unknown>>(
    '/api/v1/master/sku-attributes',
    { ...toQuery(params), page: page - 1, size: PAGE_SIZE },
    { signal },
  )
  const items = res.data?.items ?? []
  return { contents: items.map((i) => toRow(i, shipperInfo)), totalCount: res.data?.pageInfo?.totalCount ?? items.length }
}

/**
 * 도메인 key 의 뿌리. **객체 밖에 두는 것이 요점이다** — 객체 리터럴 안에서는 자기 자신을
 * 참조할 수 없어, `shipperOptions` 처럼 **즉시 평가되는 key** 가 `all` 을 spread 하지 못한다.
 * (`list` 는 함수라 호출 시점에 참조할 수 있지만, 두 key 가 다른 방식으로 쓰이면 읽기 어렵다.)
 *
 * 뿌리를 밖으로 빼면 **셋이 같은 방식으로 파생**되고, 뿌리를 바꿀 때 전부 따라온다.
 */
const QUERY_KEY_ROOT = ['SKU_ATTRIBUTE'] as const

/**
 * 목록 key 의 앞부분. **`list` 는 반드시 이것 뒤에 조건·페이지를 붙여 만든다** — `paramsOfListKey` 가 이 길이 위치에서
 * 조건을 꺼내므로, 두 key 를 따로 적으면 한쪽만 바뀌는 날 조용히 다른 원소를 읽는다 (LOGISPRD-14926)
 */
const LIST_KEY_PREFIX = [...QUERY_KEY_ROOT, MENU_CODE, 'list'] as const

/** 도메인 query key (CLAUDE.md — 인라인 문자열 리터럴 대신 객체로 모은다). */
export const skuAttributeQueryKeys = {
  all: QUERY_KEY_ROOT,
  /**
   * 목록 전체(모든 조회·페이지) — 저장 뒤 무효화 범위.
   *
   * **메뉴 코드를 넣는다** (LOGISPRD-14923). 메뉴 탭을 닫으면 `MenuOnSessionWrap.remove` 가 메뉴 코드가 든 query 만
   * 지운다. 목록은 오래 보관하므로(`LIST_CACHE_TIME_MS`) 넣지 않으면 탭을 닫았다 열어도 닫기 전 목록이 그대로 나온다
   */
  lists: LIST_KEY_PREFIX,
  list: (params: SkuAttributeParams, page: number) => [...LIST_KEY_PREFIX, params, page] as const,
  shipperOptions: [...QUERY_KEY_ROOT, 'shipperOptions'] as const,
}

/** 목록 key 에서 조회 조건을 꺼낸다 — `list` 가 `[...LIST_KEY_PREFIX, params, page]` 라 접두사 바로 뒤가 조건이다 */
const paramsOfListKey = (queryKey: readonly unknown[]): SkuAttributeParams | undefined =>
  queryKey[LIST_KEY_PREFIX.length] as SkuAttributeParams | undefined

/**
 * ### `shipperInfo` 를 `queryKey` 에 넣지 않는다 — 대신 화주 옵션을 고정한다
 *
 * `queryFn` 이 `shipperInfo` 를 클로저로 읽으므로 원칙대로면 key 에 넣어야 한다. 그런데 넣으면
 * **화주 옵션이 refetch 될 때마다 받아 둔 페이지를 전부 다시 받는다.**
 * 표시 두 열(화주타입·화주명)을 최신으로 만들려고 목록을 다시 받는 것은 맞지 않는다.
 *
 * 그래서 **[useGetShipperOptions] 를 `staleTime: Infinity` 로 고정**해 원인을 없앴다. 화주 목록은
 * 세션 중에 바뀌지 않으므로 자동 refetch 가 일어나지 않고, `shipperInfo` 의 동일성이 유지되어
 * 두 열이 낡을 창 자체가 없다.
 *
 * ### 받은 페이지는 다시 받지 않는다 (LOGISPRD-14923)
 *
 * `staleTime: Infinity` 라 이전 페이지로 돌아가거나, 다른 메뉴 탭에 갔다가 돌아와도 캐시를 그대로 보여 준다.
 * 새로 받는 길은 둘뿐이다 — [조회] 버튼(매번 새 `_seq` 라 새 key)과 저장 뒤 무효화(`useModifySkuAttributes`).
 * 화면이 사라진 동안에도 캐시가 남도록 `gcTime` 을 늘린다(`LIST_CACHE_TIME_MS`).
 *
 * **오래 두는 것은 지금 조회(`_seq`)의 페이지뿐이다.** 조회마다 새 `_seq` 라 지난 조회의 페이지는 다시 열 길이 없는데,
 * 그대로 두면 30분 동안 조회 수 × 연 페이지 수만큼 2,000행씩 쌓인다. 조회가 바뀌면 지켜보는 화면이 없는 지난 조회
 * 페이지를 바로 지운다.
 *
 * `placeholderData` 는 쓰지 않는다 — 앞 페이지 행을 보여 주는 동안 그 행을 편집·저장할 수 있고, 새 페이지가 도착하면
 * 그 입력이 확인 없이 사라진다. 받는 동안은 표가 비고, 표가 편집을 막는다(`SkuAttributeTable` 의 `isFetching`).
 */
export const useGetSkuAttributeList = (
  params: SkuAttributeParams,
  page: number,
  shipperInfo: ShipperInfoMap,
  enabled = true,
): UseQueryResult<SkuAttributeListResponse, Error> => {
  const queryClient = useQueryClient()
  const result = useQuery({
    queryKey: skuAttributeQueryKeys.list(params, page),
    queryFn: ({ signal }) => fn(params, page, shipperInfo, signal),
    enabled,
    staleTime: Infinity,
    gcTime: LIST_CACHE_TIME_MS,
    // **전역 에러 모달을 끈다.** `queryClient` 의 `QueryCache.onError` 가 이 플래그가 없는 실패에
    // 「데이터를 가져올 수 없습니다」 모달을 띄우는데, 이 화면은 테이블이 사유를 토스트로 드러낸다
    // (403 = MASTER 권한 필요). 둘 다 뜨면 **일반 문구 모달이 실제 원인을 가린다** — 403 을
    // 드러내려던 의도와 정확히 반대다. 레포 관례도 「화면이 직접 띄우면 이 플래그를 단다」이다
    meta: { noErrorMessage: true },
  })

  // **`useQuery` 뒤에 둔다** — 옵저버가 새 key 로 옮겨 가는 것도 effect 라, 앞에 두면 지난 조회의 보던 페이지가 아직
  // 지켜보는 중으로 잡혀 남는다
  //
  // **조회가 없으면(`_seq` 없음) 지우지 않는다.** 탭 재진입의 첫 렌더는 URL 조건이 없으면 빈 조회로 시작하고, 바로 뒤
  // 진입 조회가 마지막 조회를 되살린다 — 빈 조회에서 지우면 되살릴 캐시를 먼저 지워 다시 받게 된다
  const seq = params._seq
  useEffect(() => {
    if (seq == null) return
    queryClient.removeQueries({
      queryKey: skuAttributeQueryKeys.lists,
      predicate: (query) => query.getObserversCount() === 0 && paramsOfListKey(query.queryKey)?._seq !== seq,
    })
  }, [queryClient, seq])

  return result
}

/** 화주 옵션 한 건 — 응답에 서비스타입이 없어 유형별 조회를 합성해 만든다. */
export interface ShipperOptionItem {
  shipperCode: string
  shipperName: string
  serviceType: string
}

/**
 * 화주 옵션 조회 결과.
 *
 * **[failedServiceTypes] 를 함께 돌려주는 것이 요점이다** — 일부 유형만 실패했을 때 화면이
 * 그 사실을 사용자에게 알려야 한다 ([useGetShipperOptions] KDoc).
 */
export interface ShipperOptionsResult {
  items: ShipperOptionItem[]
  /** 조회에 실패한 서비스타입. 비어 있으면 전부 성공이다. */
  failedServiceTypes: string[]
}

/**
 * 화주 옵션 — **MWP·MLT 두 유형만** 묻는다.
 *
 * MA02 는 전 유형을 병렬 조회하지만 이 화면은 그 둘 밖 화주를 고르면 BE 가 빈 목록을 주므로
 * 애초에 목록에 넣지 않는다.
 *
 * `staleTime: Infinity` 인 근거는 [useGetSkuAttributeList] KDoc 에 있다 — 이 값의 동일성이
 * 목록의 표시 두 열을 좌우한다.
 *
 * ### 부분 실패를 살린다 — 다만 **조용히 넘기지 않는다**
 *
 * `Promise.all` 은 한 유형이 실패하면 전체가 에러가 된다. `staleTime: Infinity` 라 자동 재조회도
 * 없어, 롤링 배포나 회로 차단으로 한쪽만 502 가 나면 **세션 내내 화주 필터가 비어 화면을 못 쓴다.**
 *
 * 그래서 `allSettled` 로 바꿨다. **그런데 성공분만 돌려주고 끝내면 더 나쁜 형태가 된다** —
 * MLT 조회만 실패했을 때 사용자는 MWP 화주만 보고 「MLT 화주가 없구나」로 읽는다. 큰 실패가
 * 조용한 누락으로 바뀌는 것이다.
 *
 * 그래서 **실패한 유형을 함께 돌려주고 화면이 경고한다.** 둘 다 실패하면 성공분이 0 이므로
 * 던져서 에러 경로로 보낸다 — 그 경우는 부분 표시가 아니라 그냥 실패다.
 */
export const useGetShipperOptions = (): UseQueryResult<ShipperOptionsResult, Error> =>
  useQuery({
    queryKey: skuAttributeQueryKeys.shipperOptions,
    staleTime: Infinity,
    queryFn: async (): Promise<ShipperOptionsResult> => {
      const settled = await Promise.allSettled(
        SERVICE_TYPE_OPTIONS.map(({ value: type }) =>
          request
            .post<ApiResponseV2<{ items: { shipperCode: string; shipperName: string }[] }>, { logisticsServiceTypes: string[] }>(
              '/api/v1/shipper-filter-options',
              { logisticsServiceTypes: [type] },
            )
            .then((r) => (r.data?.items ?? []).map((s) => ({ ...s, serviceType: type }))),
        ),
      )

      const items: ShipperOptionItem[] = []
      const failedServiceTypes: string[] = []
      settled.forEach((result, index) => {
        if (result.status === 'fulfilled') items.push(...result.value)
        else failedServiceTypes.push(SERVICE_TYPE_OPTIONS[index].value)
      })

      // 전부 실패하면 부분 표시가 아니라 그냥 실패다 — 빈 목록을 정상처럼 돌려주지 않는다
      if (failedServiceTypes.length === SERVICE_TYPE_OPTIONS.length) {
        throw new Error('화주 목록을 가져오지 못했습니다.')
      }
      return { items, failedServiceTypes }
    },
  })
